import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextArea from '@/components/atoms/TextArea';
import TextField from '@/components/atoms/TextField';
import { SuggestNameButton } from '@/components/organisms/FishGroupForms';
import type { OverviewCage } from '@/domain/fishGroups';

import type { OperateWizardAction, WizardDestination } from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

interface TargetCardProps {
    index: number;
    destination: WizardDestination;
    emptyCages: OverviewCage[];
    sourceName: string;
    dispatch: Dispatch<OperateWizardAction>;
}

export function TargetCard(props: TargetCardProps) {
    const { index, destination, emptyCages, sourceName, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    return (
        <Card as="article" radius="sm" padding="xs" className={styles.destinationCard}>
            <header className={styles.destinationHeader}>
                <h5 className={styles.destinationTitle}>
                    {t('actions.operate.destinations.destinationLabel', { index: index + 1 })}
                </h5>
                <Button
                    variant="tertiary"
                    size="small"
                    type="button"
                    onClick={() =>
                        dispatch({ type: 'removeDestination', tempId: destination.tempId })
                    }>
                    {t('actions.operate.destinations.removeButton')}
                </Button>
            </header>

            <div className={styles.fieldsGrid}>
                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-name`}
                    label={t('actions.operate.destinations.fields.name')}
                    labelAction={
                        <SuggestNameButton
                            cageName={
                                emptyCages.find((c) => c.cageId === destination.cageId)?.cageName
                            }
                            operation="split"
                            onApply={(value) =>
                                dispatch({
                                    type: 'setDestinationName',
                                    tempId: destination.tempId,
                                    value,
                                })
                            }
                        />
                    }>
                    <TextField
                        id={`${destination.tempId}-name`}
                        type="text"
                        placeholder={t('actions.operate.split.targetNamePlaceholder', {
                            sourceName,
                            index: index + 1,
                        })}
                        value={destination.name}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationName',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-cage`}
                    label={t('actions.operate.destinations.fields.cage')}>
                    <Select
                        id={`${destination.tempId}-cage`}
                        value={destination.cageId == null ? '' : String(destination.cageId)}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationCage',
                                tempId: destination.tempId,
                                value: e.target.value === '' ? null : Number(e.target.value),
                            })
                        }>
                        <option value="">
                            {t('actions.operate.destinations.fields.cagePlaceholder')}
                        </option>
                        {emptyCages.map((cage) => (
                            <option key={cage.cageId} value={cage.cageId}>
                                {cage.cageName}
                            </option>
                        ))}
                    </Select>
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-count`}
                    label={t('actions.operate.destinations.fields.estimatedCount')}
                    hint={t('actions.operate.destinations.fields.estimatedCountHint')}>
                    <TextField
                        id={`${destination.tempId}-count`}
                        type="number"
                        min={1}
                        value={destination.estimatedFishCount}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationEstimatedCount',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-notes`}
                    label={t('actions.operate.destinations.fields.notes')}
                    className={styles['field--fullWidth']}>
                    <TextArea
                        id={`${destination.tempId}-notes`}
                        rows={2}
                        value={destination.notes}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationNotes',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>
            </div>
        </Card>
    );
}
